import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './contactus.css';

// Reusable animated section component
function AnimatedSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setIsSubmitted(true);
      setIsLoading(false);
      
      // Reset form after a delay
      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', service: '', message: '' });
        setIsSubmitted(false);
      }, 5000);
    }, 1500);
  };

  const contactMethods = [
    {
      icon: '📞',
      title: 'Call Us',
      details: ['+94 70 1111 735', '+94 76 5610 017'],
      link: 'tel:+94701111735',
      description: 'Available 24/7 for urgent inquiries'
    },
    {
      icon: '📧',
      title: 'Email Us',
      details: ['info@learnex.lk', 'support@learnex.lk'],
      link: 'mailto:info@learnex.lk',
      description: 'We respond within 24 hours'
    },
    {
      icon: '📍',
      title: 'Visit Us',
      details: ['33, Sri Dharmarama Road', 'Ratmalana, Sri Lanka'],
      link: 'https://maps.google.com/?q=33,Sri+Dharmarama+Road,Ratmalana,Sri+Lanka',
      description: 'Open 24 hours for scheduled appointments'
    },
    {
      icon: '🕒',
      title: 'Business Hours',
      details: ['24/7 Support Available', 'Office Hours: 8:00 AM - 6:00 PM'],
      link: null,
      description: 'We\'re always here to help'
    }
  ];

  const services = [
    "Custom Software Development",
    "Cloud Solutions & Migration",
    "AI & Machine Learning",
    "Cybersecurity Services",
    "IT Consulting",
    "Digital Transformation",
    "Other"
  ];

  return (
    <div className="contactus-container">
      {/* ===== HERO HEADER ===== */}
      <section className="contact-hero">
        <motion.div 
          className="contact-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="company-logo">
            <h1>LEARNEX</h1>
            <p>Intelligent Technology Solutions</p>
          </div>
          <h2>Let's Start Something <span className="gradient-text">Remarkable</span></h2>
          <p className="hero-subtitle">
            Have an idea? Let's shape it together. Our team of experts is ready to transform your vision into reality with cutting-edge IT solutions.
          </p>
        </motion.div>
        <motion.div 
          className="contact-hero-graphic"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="contact-graphic">
            <div className="floating-element element-1">💬</div>
            <div className="floating-element element-2">📞</div>
            <div className="floating-element element-3">📧</div>
            <div className="floating-element element-4">📍</div>
            <div className="main-circle">
              <div className="inner-circle">Contact</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ===== MAIN CONTENT ===== */}
      <div className="contact-content">
        {/* ===== CONTACT FORM ===== */}
        <AnimatedSection className="contact-form-section">
          <div className="form-container">
            <h2>Send Us a Message</h2>
            <p className="section-subtitle">Fill out the form below and our team will get back to you shortly</p>
            
            {isSubmitted ? (
              <motion.div 
                className="success-message"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="success-icon">✅</div>
                <h3>Message Received!</h3>
                <p>Thank you for contacting LEARNEX. We'll get back to you within 24 hours.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-grid">
                  <div className="input-group">
                    <label htmlFor="name">Your Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label htmlFor="company">Company (Optional)</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company name"
                    />
                  </div>
                  
                  <div className="input-group">
                    <label htmlFor="service">Service Interested In *</label>
                    <select 
                      id="service" 
                      name="service" 
                      value={formData.service}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service.toLowerCase().replace(/\s+/g, '-')}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="input-group full-width">
                  <label htmlFor="message">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project or inquiry..."
                  ></textarea>
                </div>
                
                <motion.button 
                  type="submit"
                  className="btn btn-primary submit-btn"
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 25px rgba(37, 99, 235, 0.3)" }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      Sending...
                    </div>
                  ) : (
                    "Send Message"
                  )}
                </motion.button>
              </form>
            )}
          </div>
        </AnimatedSection>

        {/* ===== CONTACT METHODS ===== */}
        <AnimatedSection className="contact-methods">
          <h2>Other Ways to Connect</h2>
          <p className="section-subtitle">Reach out to us through any of these channels</p>
          <div className="methods-grid">
            {contactMethods.map((method, index) => (
              method.link ? (
                <motion.a 
                  key={index}
                  href={method.link}
                  className="method-card"
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  target={method.link.includes('http') ? '_blank' : '_self'}
                  rel={method.link.includes('http') ? 'noopener noreferrer' : ''}
                >
                  <div className="method-icon">{method.icon}</div>
                  <h3>{method.title}</h3>
                  {method.details.map((detail, i) => (
                    <p key={i} className="method-detail">{detail}</p>
                  ))}
                  <p className="method-description">{method.description}</p>
                </motion.a>
              ) : (
                <motion.div 
                  key={index}
                  className="method-card"
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="method-icon">{method.icon}</div>
                  <h3>{method.title}</h3>
                  {method.details.map((detail, i) => (
                    <p key={i} className="method-detail">{detail}</p>
                  ))}
                  <p className="method-description">{method.description}</p>
                </motion.div>
              )
            ))}
          </div>
        </AnimatedSection>
      </div>

      {/* ===== MAP SECTION ===== */}
      <AnimatedSection className="map-section">
        <h2>Find Us Here</h2>
        <p className="section-subtitle">Visit our office at Ratmalana for a consultation</p>
        <div className="map-container">
          {/* In a real project, integrate Google Maps or Mapbox here */}
          <div className="map-placeholder">
            <div className="map-content">
              <div className="pin">📍</div>
              <h3>LEARNEX Technologies</h3>
              <p>33, Sri Dharmarama Road</p>
              <p>Ratmalana, Sri Lanka</p>
              <a 
                href="https://maps.google.com/?q=33,Sri+Dharmarama+Road,Ratmalana,Sri+Lanka" 
                target="_blank" 
                rel="noopener noreferrer"
                className="map-link"
              >
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== CTA SECTION ===== */}
      <AnimatedSection className="contact-cta">
        <h2>Ready to Transform Your Business?</h2>
        <p>Schedule a free consultation with our experts today</p>
        <motion.button 
          className="btn btn-primary"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(37, 99, 235, 0.3)" }}
          whileTap={{ scale: 0.95 }}
        >
          Schedule a Call
        </motion.button>
      </AnimatedSection>
    </div>
  );
}

export default ContactUs;